import { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Wrench, MessageSquare, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { useTeams } from '../../rocks/hooks/useTeams';
import {
  useAgentTeamAssignments,
  useUpsertMastraAssignment,
  useDeleteMastraAssignment,
} from '../hooks/useMastraTeamAssignments';
import { fetchAgents } from '../api/agents';

type AgentsResponse = Awaited<ReturnType<typeof fetchAgents>>;
type MastraAgent = AgentsResponse[string];

interface AgentListItem {
  id: string;
  name: string;
  description: string;
  toolCount: number;
  teamId: string | undefined;
  teamName: string | undefined;
}

const PANEL_MIN = 350;
const PANEL_MAX_PCT = 0.6;
const PANEL_STORAGE_KEY = 'atek_agents_panel_width';

// AgentInstructions can be a string or a SystemMessage object at runtime
function instructionsText(instructions: unknown): string {
  if (!instructions) return '';
  if (typeof instructions === 'string') return instructions;
  if (typeof instructions === 'object' && instructions !== null && 'content' in instructions) {
    const { content } = instructions as { content: unknown };
    if (typeof content === 'string') return content;
  }
  return '';
}

function toListItems(
  data: AgentsResponse | undefined,
  assignmentMap: Record<string, string>,
  teamsMap: Record<string, string>,
): AgentListItem[] {
  if (!data) return [];
  return Object.entries(data).map(([id, agent]) => ({
    id,
    name: agent.name || id,
    description: agent.description || instructionsText(agent.instructions).slice(0, 120) || '',
    toolCount: Object.keys(agent.tools ?? {}).length,
    teamId: assignmentMap[id],
    teamName: assignmentMap[id] ? teamsMap[assignmentMap[id]] : undefined,
  }));
}

export function AgentsListPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['mastra-agents'],
    queryFn: fetchAgents,
  });
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [selectedAgent, setSelectedAgent] = useState<MastraAgent | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const navigate = useNavigate();
  const { data: teams } = useTeams();
  const { data: assignments } = useAgentTeamAssignments();
  const upsertAssignment = useUpsertMastraAssignment();
  const deleteAssignment = useDeleteMastraAssignment();

  // Draggable panel
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem(PANEL_STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth || (containerRef.current ? containerRef.current.offsetWidth * 0.4 : 450);

    const onMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const delta = startX - ev.clientX;
      const containerWidth = containerRef.current.offsetWidth;
      const newWidth = Math.min(Math.max(startWidth + delta, PANEL_MIN), containerWidth * PANEL_MAX_PCT);
      setPanelWidth(newWidth);
    };

    const onUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setPanelWidth((w) => { localStorage.setItem(PANEL_STORAGE_KEY, String(w)); return w; });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  const handleSelectAgent = useCallback((agent: MastraAgent) => {
    setSelectedAgent(agent);
    setPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedAgent(null);
    setPanelOpen(false);
  }, []);

  const assignmentMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (assignments) {
      for (const a of assignments) {
        map[a.entity_id] = a.team_id;
      }
    }
    return map;
  }, [assignments]);

  const teamsMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (teams) {
      for (const t of teams) {
        map[t.id] = t.name;
      }
    }
    return map;
  }, [teams]);

  const items = useMemo(() => {
    let filtered = toListItems(data, assignmentMap, teamsMap);
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.id.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q),
      );
    }
    if (teamFilter !== 'all') {
      filtered = filtered.filter((a) => a.teamId === teamFilter);
    }
    return filtered;
  }, [data, search, teamFilter, assignmentMap, teamsMap]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-400" />
          <h1 className="text-xl font-semibold">Agents</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {(teams || []).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search agents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/50 p-4 text-sm text-red-300">
          Failed to load agents. Is Mastra running?
        </div>
      )}

      <div ref={containerRef} className="flex min-h-[calc(100vh-240px)]">

      {/* Left: table content */}
      <div className="flex-1 min-w-0 overflow-auto">
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead className="w-40">Team</TableHead>
                <TableHead className="text-center w-20">
                  <div className="flex items-center justify-center gap-1">
                    <Wrench className="h-3.5 w-3.5" />
                    Tools
                  </div>
                </TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 2 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 w-28 animate-pulse rounded bg-muted" /></TableCell>
                    <TableCell><div className="h-4 w-24 animate-pulse rounded bg-muted" /></TableCell>
                    <TableCell><div className="h-4 w-24 animate-pulse rounded bg-muted" /></TableCell>
                    <TableCell className="text-center"><div className="h-4 w-6 mx-auto animate-pulse rounded bg-muted" /></TableCell>
                    <TableCell><div className="h-4 w-48 animate-pulse rounded bg-muted" /></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    {search ? 'No agents match your search.' : 'No agents found.'}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((agent) => {
                  const agentData = data?.[agent.id];
                  return (
                  <TableRow
                    key={agent.id}
                    className={`cursor-pointer hover:bg-muted/50 ${selectedAgent?.id === agent.id ? 'bg-muted/70' : ''}`}
                    onClick={() => agentData && handleSelectAgent(agentData)}
                  >
                    <TableCell className="font-medium text-blue-300">{agent.name}</TableCell>
                    <TableCell className="font-mono text-muted-foreground text-sm">{agent.id}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={agent.teamId || 'none'}
                        onValueChange={(value) => {
                          if (value === 'none') {
                            deleteAssignment.mutate({ entityType: 'agent', entityId: agent.id });
                          } else {
                            upsertAssignment.mutate({ entityType: 'agent', entityId: agent.id, teamId: value });
                          }
                        }}
                      >
                        <SelectTrigger className="w-[130px] h-7 text-xs">
                          <SelectValue placeholder="No team" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No team</SelectItem>
                          {(teams || []).map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-center">{agent.toolCount}</TableCell>
                    <TableCell className="text-muted-foreground max-w-md truncate">{agent.description}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/ai/chat/${agent.id}`)}
                        className="p-1 rounded hover:bg-blue-400/10 transition-colors"
                        title="Open chat"
                      >
                        <MessageSquare className="h-4 w-4 text-blue-400" />
                      </button>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

        {/* Drag handle + detail panel */}
        {panelOpen && selectedAgent && (
          <>
            <div
              className="w-1.5 shrink-0 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 transition-colors bg-border rounded-full my-2"
              onMouseDown={handleDragStart}
            />
            <div className="shrink-0 min-h-0 h-full flex flex-col border-l bg-background overflow-hidden" style={{ width: panelWidth || '40%' }}>
              {/* Panel header */}
              <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                <span className="text-sm font-semibold truncate">{selectedAgent.name || selectedAgent.id}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClosePanel}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {/* Panel body */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Agent ID</Label>
                    <p className="text-sm font-mono mt-1">{selectedAgent.id}</p>
                  </div>
                  {selectedAgent.name && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Name</Label>
                      <p className="text-sm mt-1">{selectedAgent.name}</p>
                    </div>
                  )}
                  {selectedAgent.description && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Description</Label>
                      <p className="text-sm mt-1 whitespace-pre-wrap">{selectedAgent.description}</p>
                    </div>
                  )}
                  {instructionsText(selectedAgent.instructions) && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Instructions</Label>
                      <p className="text-xs mt-1 text-muted-foreground whitespace-pre-wrap max-h-32 overflow-y-auto bg-muted/30 p-2 rounded">
                        {instructionsText(selectedAgent.instructions)}
                      </p>
                    </div>
                  )}
                  {Object.keys(selectedAgent.tools || {}).length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Tools ({Object.keys(selectedAgent.tools).length})</Label>
                      <div className="mt-2 space-y-2">
                        {Object.entries(selectedAgent.tools).map(([toolId, tool]) => (
                          <div key={toolId} className="bg-muted/50 p-2 rounded text-xs">
                            <p className="font-mono font-medium">{tool.id}</p>
                            <p className="text-muted-foreground mt-1">{tool.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
